/**
 * Timed Effect Base Class for Poker Reborn Effects Engine
 *
 * Base class for effects with a fixed duration, using requestAnimationFrame.
 *
 * When the browser tab becomes hidden, effects instantly complete (snap to end)
 * to prevent timeouts and ensure consistent game state.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 2.2.8
 */

import type { EffectTarget } from './EffectTypes';
import { EffectType, EffectState } from './EffectTypes';
import { BaseEffect } from './BaseEffect';
import type { EffectOptions } from './Effect';

// =============================================================================
// TIMED EFFECT CLASS (P2.2.8)
// =============================================================================

/**
 * Base class for effects that play over a fixed duration.
 *
 * Uses requestAnimationFrame for smooth animations.
 * Subclasses implement onTick(progress) for the actual animation.
 *
 * Visibility-aware: When the tab becomes hidden, the effect instantly
 * completes to prevent rAF throttling from causing timeouts.
 */
export abstract class TimedEffect extends BaseEffect {
  private _animationFrameId: number | null = null;
  private _visibilityHandler: (() => void) | null = null;

  constructor(
    name: string,
    target: EffectTarget,
    duration: number,
    options: EffectOptions = {}
  ) {
    super(name, EffectType.ANIMATION, target, {
      ...options,
      duration,
    });
  }

  // ---------------------------------------------------------------------------
  // Lifecycle Implementation
  // ---------------------------------------------------------------------------

  protected onPlay(): void {
    // Listen for tab visibility changes to handle rAF throttling
    this._visibilityHandler = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this._visibilityHandler);

    this.tick();
  }

  protected onCancel(): void {
    this.cleanupAnimation();

    // Snap to end state
    this.onTick(1);
  }

  protected override onPause(): void {
    if (this._animationFrameId !== null) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }
  }

  protected override onResume(): void {
    this.tick();
  }

  // ---------------------------------------------------------------------------
  // Animation Loop
  // ---------------------------------------------------------------------------

  private tick = (): void => {
    if (this._state !== EffectState.PLAYING) {
      return;
    }

    const elapsed = this.getElapsedTime();
    const progress = Math.min(1, elapsed / this.duration);

    this.updateProgress(progress);
    this.onTick(progress);

    if (progress >= 1) {
      this.cleanupAnimation();
      this.complete();
    } else {
      this._animationFrameId = requestAnimationFrame(this.tick);
    }
  };

  // ---------------------------------------------------------------------------
  // Visibility Handling
  // ---------------------------------------------------------------------------

  /**
   * Handle tab visibility changes.
   * When tab becomes hidden, instantly complete the effect to prevent
   * rAF throttling from causing timeouts.
   */
  private handleVisibilityChange(): void {
    if (document.hidden && this._state === EffectState.PLAYING) {
      // Tab became hidden - snap to end and complete
      this.cleanupAnimation();
      this.onTick(1);
      this.complete();
    }
  }

  /**
   * Clean up animation frame and visibility listener.
   */
  private cleanupAnimation(): void {
    if (this._animationFrameId !== null) {
      cancelAnimationFrame(this._animationFrameId);
      this._animationFrameId = null;
    }

    if (this._visibilityHandler !== null) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Abstract Methods
  // ---------------------------------------------------------------------------

  /**
   * Called each frame with the current progress (0-1).
   * Implement your animation logic here.
   */
  protected abstract onTick(progress: number): void;
}
